import { get, set, cloneDeep } from 'lodash';
import type { FormItem } from '../FormProps';
import { fixDateTZ } from '../../DataGrid/utils';

/**
 * Extracts date-only field paths from form field definitions.
 */
const extractDateOnlyFields = <T>(fields: FormItem<T>[]): string[] => {
  const dateFields: string[] = [];

  const processItem = (item: FormItem<T>) => {
    // Check if it's a field with DateBox and type 'date'
    if ('dataField' in item && item.dataField) {
      if (item.editorType === 'DateBox' && item.editorOptions?.type === 'date') {
        dateFields.push(String(item.dataField));
      }
    }

    // Process nested items in groups
    if ('items' in item && Array.isArray(item.items)) {
      item.items.forEach(processItem);
    }
  };

  fields.forEach(processItem);
  return dateFields;
};

/**
 * Fixes date-only fields in form data before submission.
 * Returns a new object with fixed dates (does not mutate the original).
 *
 * @param formData - The form data to process
 * @param fields - The form field definitions to identify date-only fields
 * @returns A new object with date values adjusted for correct UTC serialization
 */
export const fixDateFieldsForSubmit = <T extends Record<string, any>>(
  formData: T,
  fields: FormItem<T>[],
): T => {
  const fixedData = cloneDeep(formData);
  const dateOnlyFields = extractDateOnlyFields(fields);

  if (dateOnlyFields.length === 0) {
    return fixedData;
  }

  for (const fieldPath of dateOnlyFields) {
    const value = get(fixedData, fieldPath) as unknown;
    if (value instanceof Date) {
      set(fixedData, fieldPath, fixDateTZ(value));
    } else if (typeof value === 'string' && value) {
      // Handle string dates that might need fixing
      const dateValue = new Date(value);
      if (!Number.isNaN(dateValue.getTime())) {
        set(fixedData, fieldPath, fixDateTZ(dateValue));
      }
    }
  }

  return fixedData;
};
